import { Money, dec } from "@despejo/domain";

/**
 * Régimen de Tributación Simplificada (plan §16, docs/tax/RTS.md).
 * Quarterly tax = factor × base (Decreto 43881-H arts. 4-5). Factors are a
 * versioned parameter (DGT may change them, art. 12); a DRAFT factor table
 * makes the result provisional.
 */

export const RTS_ENGINE_VERSION = "rts@1.0.0";

export type RtsActivity =
  | "a" | "b" | "c" | "d" | "e" | "f" | "g" | "h" | "i" | "j" | "k" | "l" | "m" | "n" | "o" | "p" | "q" | "r" | "s" | "t" | "u" | "v";

/** Per activity: renta factor + IVA factor, or per-rate IVA factors for retail (a) and feria farmers (f). */
export interface RtsFactor {
  renta: string;
  iva?: string;
  ivaByRate?: Record<string, string>;
  /** Taxi: colones per km for renta. */
  rentaPerKm?: string;
}

export interface RtsFactorTable {
  version: string;
  approved: boolean;
  factors: Partial<Record<RtsActivity, RtsFactor>>;
}

export interface RtsBase {
  /** Purchases in the quarter INCLUDING IVA, by the rate borne ("0.13", "0.02", "0.01", "0" for untaxed). */
  purchasesByRate: Record<string, string>;
  /** Services providers (b,c,q,r,s): personal/professional services and salaries paid. */
  servicesAndSalaries?: string;
  /** Producers (d-p): labour and other production costs (electricity, phone, water, rent). */
  productionCosts?: string;
  /** Fishing (t,u): fuel purchases. */
  fuel?: string;
  /** Taxi (v): km driven. */
  km?: string;
  /** First quarter in RTS: inventory on hand at entry (art. 9). */
  openingInventory?: string;
}

export interface RtsResult {
  engine: string;
  factorVersion: string;
  provisional: boolean;
  base: string;
  iva: string;
  renta: string;
  trace: { item: string; amount: string; factor?: string }[];
  exceptions: string[];
}

const SERVICES = new Set<RtsActivity>(["b", "c", "q", "r", "s"]);
const PRODUCERS = new Set<RtsActivity>(["d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p"]);

export function computeRts(activity: RtsActivity, b: RtsBase, table: RtsFactorTable): RtsResult {
  const f = table.factors[activity];
  const trace: RtsResult["trace"] = [];
  const exceptions: string[] = [];
  if (!f) {
    return { engine: RTS_ENGINE_VERSION, factorVersion: table.version, provisional: true, base: "0.00", iva: "0.00", renta: "0.00", trace, exceptions: [`No hay factor para la actividad ${activity}`] };
  }
  if (activity === "v") {
    const km = Money.of(b.km ?? "0");
    const renta = km.times(f.rentaPerKm ?? "0");
    trace.push({ item: "km", amount: km.toString(), factor: f.rentaPerKm });
    return { engine: RTS_ENGINE_VERSION, factorVersion: table.version, provisional: !table.approved, base: km.toFixed(2), iva: "0.00", renta: renta.round(2).toFixed(2), trace, exceptions };
  }
  let base = Money.zero();
  let iva = Money.zero();
  if (activity === "t" || activity === "u") {
    base = Money.of(b.fuel ?? "0");
    trace.push({ item: "combustible", amount: base.toString() });
  } else {
    for (const [rate, amt] of Object.entries(b.purchasesByRate)) {
      const m = Money.of(amt);
      base = base.add(m);
      trace.push({ item: `compras ${rate}`, amount: m.toString() });
      if (f.ivaByRate) {
        const factor = f.ivaByRate[rate];
        if (factor === undefined) {
          if (!dec(rate).isZero()) exceptions.push(`Sin factor de IVA para compras a tarifa ${rate}`);
        } else {
          iva = iva.add(m.times(factor));
          trace.push({ item: `IVA compras ${rate}`, amount: m.times(factor).toString(), factor });
        }
      }
    }
    if (b.openingInventory) {
      base = base.add(Money.of(b.openingInventory));
      trace.push({ item: "inventario inicial (art. 9)", amount: b.openingInventory });
    }
    if (SERVICES.has(activity) && b.servicesAndSalaries) {
      base = base.add(Money.of(b.servicesAndSalaries));
      trace.push({ item: "servicios y salarios", amount: b.servicesAndSalaries });
    }
    if (PRODUCERS.has(activity) && b.productionCosts) {
      base = base.add(Money.of(b.productionCosts));
      trace.push({ item: "costos de producción", amount: b.productionCosts });
    }
  }
  if (!f.ivaByRate) iva = base.times(f.iva ?? "0");
  const renta = base.times(f.renta);
  trace.push({ item: "renta", amount: renta.toString(), factor: f.renta });
  return {
    engine: RTS_ENGINE_VERSION,
    factorVersion: table.version,
    provisional: !table.approved,
    base: base.round(2).toFixed(2),
    iva: iva.round(2).toFixed(2),
    renta: renta.round(2).toFixed(2),
    trace,
    exceptions,
  };
}

// ------------------------------------------------------------ eligibility

export interface RtsLimits {
  purchasesSalariosBase: string;
  fixedAssetsSalariosBase: string;
  maxPersons: number;
  maxEstablishments: number;
}

export interface EligibilityInput {
  salarioBase: string;
  purchasesYtd: string;
  /** Months elapsed in the year (1-12) for the linear projection. */
  monthsElapsed: number;
  fixedAssets: string;
  persons: number;
  establishments: number;
  activityEligible: boolean;
}

export interface EligibilityCheck {
  name: string;
  used: string;
  limit: string;
  pct: number;
  projectedPct?: number;
  status: "OK" | "WATCH" | "BREACH";
}

/** Plan §16.2: warn on projection, not only when a limit is crossed. */
export function rtsEligibility(i: EligibilityInput, limits: RtsLimits): { checks: EligibilityCheck[]; overall: "OK" | "WATCH" | "BREACH" } {
  const sb = Money.of(i.salarioBase);
  const pct = (used: Money, limit: Money) => (limit.isZero() ? 0 : Number(used.dividedBy(limit.amount.toString()).times(100).round(0).toString()));
  const status = (p: number, proj?: number): EligibilityCheck["status"] => (p > 100 ? "BREACH" : (proj ?? p) > 90 ? "WATCH" : "OK");
  const purchLimit = sb.times(limits.purchasesSalariosBase);
  const ytd = Money.of(i.purchasesYtd);
  const projected = i.monthsElapsed > 0 ? ytd.times(12).dividedBy(i.monthsElapsed) : ytd;
  const pp = pct(ytd, purchLimit);
  const ppp = pct(projected, purchLimit);
  const assetsLimit = sb.times(limits.fixedAssetsSalariosBase);
  const ap = pct(Money.of(i.fixedAssets), assetsLimit);
  const checks: EligibilityCheck[] = [
    { name: "Actividad permitida", used: i.activityEligible ? "sí" : "no", limit: "sí", pct: i.activityEligible ? 0 : 101, status: i.activityEligible ? "OK" : "BREACH" },
    { name: "Compras del año", used: ytd.toFixed(2), limit: purchLimit.toFixed(2), pct: pp, projectedPct: ppp, status: status(pp, ppp) },
    { name: "Personas", used: String(i.persons), limit: String(limits.maxPersons), pct: Math.round((i.persons / limits.maxPersons) * 100), status: i.persons > limits.maxPersons ? "BREACH" : i.persons === limits.maxPersons ? "WATCH" : "OK" },
    { name: "Establecimientos", used: String(i.establishments), limit: String(limits.maxEstablishments), pct: Math.round((i.establishments / limits.maxEstablishments) * 100), status: i.establishments > limits.maxEstablishments ? "BREACH" : "OK" },
    { name: "Activos fijos", used: Money.of(i.fixedAssets).toFixed(2), limit: assetsLimit.toFixed(2), pct: ap, status: status(ap) },
  ];
  const overall = checks.some((c) => c.status === "BREACH") ? "BREACH" : checks.some((c) => c.status === "WATCH") ? "WATCH" : "OK";
  return { checks, overall };
}
